import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Nav, Tab, Table, Badge, Dropdown, ListGroup, Modal } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select'; // Import react-select
import { agentApi, productApi, notificationApi, searchApi,dsoApi } from '../../services/api'; // Import productApi and notification API
import { locationApi } from '../../services/location';
import BusinessLoanDocuments from '../../components/BusinessLoanDocuments';
import './LeadDetails.css';
import { FaArrowLeft, FaSave, FaUser, FaEnvelope, FaPhone, FaIdCard, FaAddressCard, FaBuilding, FaGraduationCap, FaHome, FaInfoCircle, FaFileAlt, FaMapMarkerAlt, FaMoneyBillWave, FaPlus, FaBriefcase, FaCheckCircle, FaFileInvoiceDollar, FaCreditCard, FaLock, FaTimesCircle, FaSearch, FaTimes, FaBell, FaQuestionCircle, FaSignOutAlt, FaUserCircle, FaChartLine } from 'react-icons/fa';
import Swal from 'sweetalert2';
import AgentSidebar from '../../components/AgentSidebar';
import SearchResults from '../../components/SearchResults';
import { useAuth } from '../../hooks/useAuth';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long'
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).replace(/^(\d+:\d+)(:\d+)?\s*/, '$1 '); // Clean seconds if present
    return `${formattedDate}, ${formattedTime}`;
  } catch (e) {
    return dateString; // Fallback
  }
};

function LeadDetails() {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const { getCurrentUser, logout } = useAuth();
  const currentUser = getCurrentUser();
  
  const [lead, setLead] = useState(null);
  // Debug state to track property fields
  const [debugPropertyFields, setDebugPropertyFields] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [cases, setCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);
  
  // Location data states
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  
  // State for office addresses
  const [officeCities, setOfficeCities] = useState([]);
  const [officePincodes, setOfficePincodes] = useState([]); // State for office pincodes
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  
  // State for case selection
  const [selectedCases, setSelectedCases] = useState([]);
  const [selectAllCases, setSelectAllCases] = useState(false);
  
  // State for permanent address
  const [permanentStates, setPermanentStates] = useState([]);
  const [permanentCities, setPermanentCities] = useState([]);
  const [permanentPincodes, setPermanentPincodes] = useState([]);
  
  // State to track the active tab
  const [activeTab, setActiveTab] = useState('personal');

  // State variables for document uploading
  const [documents, setDocuments] = useState({
    adhaar_card_file: null,
    pan_card_file: null,
    voter_id_card_file: null,
    bank_statements_file: null,
    bank_soa_file: null,
    form_16_file: null,
    office_id_card_file: null
  });
  const [uploading, setUploading] = useState({});
  const [uploadError, setUploadError] = useState({});

  // Added states for product selection and push to backoffice
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [canPushToBackoffice, setCanPushToBackoffice] = useState(false);
  
  // Add success alert state
  const [successAlert, setSuccessAlert] = useState({
    show: false,
    message: ''
  });
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    fetchLead();
    fetchStates();
    fetchLeadStatuses(); // Add this call
    fetchProducts(); // Fetch products
    if(leadId) {
      fetchDocuments(); // Fetch documents
      fetchLoanDocuments();
    }
    
    document.title = 'Agent Dashboard - Lead Details';
  }, [leadId]);
  
  // Notifications polling effect
  useEffect(() => {
    let intervalId;
    const fetchNotificationsData = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };

    fetchNotificationsData(); // Initial fetch for count
    intervalId = setInterval(fetchNotificationsData, 60000); // Poll count every 60 seconds

    return () => clearInterval(intervalId); // Cleanup interval
  }, []); // Run once for interval setup

  // Fetch full notifications specifically when dropdown opens
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 }); // Fetch latest 10
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications for dropdown:', error);
          setNotifications([]); // Clear notifications on error
        } finally {
          setLoadingNotifications(false);
        }
      }
    };
    fetchFullNotifications();
  }, [showNotifications]); // Trigger when dropdown visibility changes

  // Handle clicks outside notification dropdown and search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      
      if (searchRef.current && !searchRef.current.contains(event.target) && !showSearchModal) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchModal]);
  
  // Search with debounce
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // If search query is empty, clear results
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    // Set a new timeout for search
    const timeout = setTimeout(() => {
      performSearch(searchQuery);
    }, 500); // 500ms debounce
    
    setSearchTimeout(timeout);
    
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchQuery]);
  
  // Effect to redirect to application tab when lead status is 7 or above
  useEffect(() => {
    if (lead?.lead_status >= 7 && activeTab !== "application") {
      setActiveTab("application");
    }
  }, [lead?.lead_status, activeTab]);
  
  useEffect(() => {
    if (leadId) {
      fetchLeadCases();
    }
  }, [leadId]);
  
  // Reset selected cases when cases change
  useEffect(() => {
    setSelectedCases([]);
    setSelectAllCases(false);
  }, [cases]);

  // Set default DOB to 19 years ago if not present
  useEffect(() => {
    if (lead && !lead.date_of_birth) {
      const nineteenYearsAgo = new Date();
      nineteenYearsAgo.setFullYear(nineteenYearsAgo.getFullYear() - 19);
      const formattedDate = nineteenYearsAgo.toISOString().split('T')[0];
      
      setLead(prev => ({
        ...prev,
        date_of_birth: formattedDate
      }));
    }
  }, [lead]);

  const fetchLeadCases = async () => {
    setLoadingCases(true);
    try {
      const response = await agentApi.getLeadCases(leadId);
      if (response.data && response.data.items) {
        setCases(response.data.items);
      } else {
        setCases([]);
      }
    } catch (err) {
      console.error('Error fetching lead cases:', err);
      setCases([]);
    } finally {
      setLoadingCases(false);
    }
  };

  const fetchLead = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await agentApi.getLeadById(leadId);
      const leadData = response.data;
      setLead(leadData);

      // Fetch initial dependent data AFTER setting lead state
      if (leadData.state_id) {
        await fetchCities(leadData.state_id);
      }
      if (leadData.city_id) {
        await fetchPincodes(leadData.city_id);
      }
      if (leadData.office_state_id) {
        await fetchOfficeCities(leadData.office_state_id);
      }
      if (leadData.office_city_id) {
        await fetchOfficePincodes(leadData.office_city_id);
      }
      
      // Fetch permanent address dependent data
      if (!leadData.perma_address_same && leadData.permanent_state_id) {
        await fetchPermanentCities(leadData.permanent_state_id);
      }
      if (!leadData.perma_address_same && leadData.permanent_city_id) {
        await fetchPermanentPincodes(leadData.permanent_city_id);
      }

    } catch (err) {
      console.error('Error fetching lead:', err);
      setError('Failed to load lead details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    setLoadingLocations(true);
    try {
      const response = await locationApi.getStates();
      // Ensure we access the 'states' array within the response data
      setStates(response.data.states || []); 
    } catch (err) {
      console.error('Error fetching states:', err);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchCities = async (stateId) => {
    try {
      const response = await locationApi.getCities(stateId);
      // Assuming the API returns { cities: [...] } or similar
      setCities(response.data.cities || []);
    } catch (err) {
      console.error('Error fetching cities:', err);
      setCities([]); // Ensure cities is an array on error
    }
  };

  const fetchPincodes = async (cityId) => {
    try {
      const response = await locationApi.getPincodes(cityId);
      // Assuming the API returns { pincodes: [...] } or similar
      setPincodes(response.data.pincodes || []);
    } catch (err) {
      console.error('Error fetching pincodes:', err);
      setPincodes([]); // Ensure pincodes is an array on error
    }
  };

  const fetchOfficeCities = async (stateId) => {
    try {
      const response = await locationApi.getCities(stateId);
      // Assuming the API returns { cities: [...] } or similar
      setOfficeCities(response.data.cities || []);
    } catch (err) {
      console.error('Error fetching office cities:', err);
      setOfficeCities([]); // Ensure officeCities is an array on error
    }
  };
  
  const fetchOfficePincodes = async (cityId) => { // Function to fetch office pincodes
    try {
      const response = await locationApi.getPincodes(cityId);
      setOfficePincodes(response.data.pincodes || []);
    } catch (err) {
      console.error('Error fetching office pincodes:', err);
      setOfficePincodes([]); 
    }
  };

  const fetchPermanentCities = async (stateId) => {
    try {
      const response = await locationApi.getCities(stateId);
      setPermanentCities(response.data.cities || []);
    } catch (err) {
      console.error('Error fetching permanent cities:', err);
      setPermanentCities([]);
    }
  };

  const fetchPermanentPincodes = async (cityId) => {
    try {
      const response = await locationApi.getPincodes(cityId);
      setPermanentPincodes(response.data.pincodes || []);
    } catch (err) {
      console.error('Error fetching permanent pincodes:', err);
      setPermanentPincodes([]);
    }
  };

  // Add this function definition
  const fetchLeadStatuses = async () => {
    setLoadingStatuses(true);
    try {
      // Use the correct API service function
      const response = await agentApi.getLeadStatusTypes(); // Use the new DSO-specific function
      // Assuming the API returns the array directly in response.data or response.data.items
      // Ensure the backend returns the array directly or adjust access (e.g., response.data.items)
      setLeadStatuses(response.data || []); // Assuming backend returns array directly
    } catch (err) {
      console.error('Error fetching lead statuses:', err);
      setError(prev => (prev ? prev + '\n' : '') + 'Failed to load lead statuses.'); // Append error
      setLeadStatuses([]);
    } finally {
      setLoadingStatuses(false);
    }
  };

  // Fetch products available to this agent
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await productApi.getProducts({ limit: 100 });
      if (response.data && response.data.items) {
        setProducts(response.data.items);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Function to fetch and populate document data from the backend
  const fetchDocuments = async () => {
    try {
      const response = await agentApi.getLeadDocuments(leadId);
      if (response.data) {
        const newDocState = { 
          adhaar_card_file: null,
          pan_card_file: null,
          voter_id_card_file: null,
          bank_statements_file: null,
          bank_soa_file: null,
          form_16_file: null,
          office_id_card_file: null,
          property_ats_title_file: null,
          loan_declaration_sheet_file: null,
          additional_documents: null
        };
        
        // Direct mapping from API response - the backend now returns document URLs directly
        if (response.data.adhaar_card_file) {
          newDocState.adhaar_card_file = { 
            name: 'Aadhaar Card', 
            url: response.data.adhaar_card_file 
          };
        }
        
        if (response.data.pan_card_file) {
          newDocState.pan_card_file = { 
            name: 'PAN Card', 
            url: response.data.pan_card_file 
          };
        }
        
        if (response.data.voter_id_card_file) {
          newDocState.voter_id_card_file = { 
            name: 'Address Proof', 
            url: response.data.voter_id_card_file 
          };
        }
        
        if (response.data.bank_statements_file) {
          newDocState.bank_statements_file = { 
            name: 'Bank Statements', 
            url: response.data.bank_statements_file 
          };
        }
        
        // Add support for Personal Loan and LAP document types
        if (response.data.bank_soa_file) {
          newDocState.bank_soa_file = { 
            name: 'Bank SOA (For BT)', 
            url: response.data.bank_soa_file 
          };
        }
        
        if (response.data.form_16_file) {
          newDocState.form_16_file = { 
            name: 'Form 16', 
            url: response.data.form_16_file 
          };
        }
        
        if (response.data.office_id_card_file) {
          newDocState.office_id_card_file = { 
            name: 'Office ID Card', 
            url: response.data.office_id_card_file 
          };
        }
        
        setDocuments(newDocState);
        console.log("Documents loaded:", newDocState); // Debug log
      }
    } catch (err) {
      console.error('Error fetching lead documents:', err);
    }
  };

  const fetchLoanDocuments = async () => {
    setLoading(true);
    try {
      console.log("Fetching loan documents for lead:", leadId);
      
      try {
        const response = await dsoApi.getLoanDocuments(leadId);
        console.log("Loan documents response (plural):", response);
        if (response.data) {
          setDocuments(prevDocs => ({
            ...prevDocs,        // keep existing docs
            ...response.data    // override/add new docs
          }));
        }
      } catch (pluralErr) {
        console.log("Error fetching loan documents, creating new record:", pluralErr);
      }
    } catch (err) {
      console.error('Error handling loan documents:', err);
      setError('Failed to initialize loan documents. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };
  // Check if all required fields are filled
  const checkRequiredFields = () => {
    // Basic required fields check
    const requiredFields = [
      // Personal details
      lead?.name, lead?.date_of_birth, lead?.gender,
      // Location details
      lead?.address, lead?.state_id, lead?.city_id, lead?.pincode_id,
      // Income details
      lead?.income, lead?.company_name, lead?.designation, 
      // At least one document
      documents.adhaar_card_file || documents.pan_card_file
    ];
    
    const isComplete = requiredFields.every(field => Boolean(field));
    const hasSelectedProducts = selectedProducts.length > 0;
    
    setCanPushToBackoffice(isComplete && hasSelectedProducts);
    return isComplete && hasSelectedProducts;
  };

  // Case type with icons for UI display
  const caseTypeWithIcons = {
    'Credit Card': { id: 1, icon: <FaCreditCard className="me-2" /> },
    'Personal Loan': { id: 2, icon: <FaMoneyBillWave className="me-2" /> },
    'Home Loan': { id: 3, icon: <FaHome className="me-2" /> },
    'Business Loan': { id: 4, icon: <FaMoneyBillWave className="me-2" /> },
    'LAP': { id: 5, icon: <FaHome className="me-2" /> }
  };

  // Handle product selection
  const handleProductSelection = (productId, isChecked) => {
    if (isChecked) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };

  // Effect to update canPushToBackoffice when selectedProducts changes
  useEffect(() => {
    // Enable Push to Backoffice button when at least one product is selected
    setCanPushToBackoffice(selectedProducts.length > 0);
  }, [selectedProducts]);

  // Handle single case selection
  const handleCaseSelection = (caseId) => {
    setSelectedCases(prev => {
      if (prev.includes(caseId)) {
        // If already selected, remove it
        const newSelection = prev.filter(id => id !== caseId);
        // Update selectAll state based on if all cases are selected
        setSelectAllCases(newSelection.length === cases.length);
        return newSelection;
      } else {
        // Add to selection
        const newSelection = [...prev, caseId];
        // Update selectAll state based on if all cases are selected
        setSelectAllCases(newSelection.length === cases.length);
        return newSelection;
      }
    });
  };

  // Handle select all cases
  const handleSelectAllCases = () => {
    const newSelectAll = !selectAllCases;
    setSelectAllCases(newSelectAll);
    
    if (newSelectAll) {
      // Select all cases
      const allCaseIds = cases.map(caseItem => caseItem.case_id);
      setSelectedCases(allCaseIds);
    } else {
      // Deselect all cases
      setSelectedCases([]);
    }
  };

  // Push to backoffice
  const handlePushToBackoffice = async () => {
    // Check if any cases are selected
    if (selectedCases.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Cases Selected',
        text: 'Please select at least one case to push to backoffice.',
        confirmButtonColor: '#343a40'
      });
      return;
    }
    
    // Make sure we're sending an array of integers, not null values
    const validCaseIds = selectedCases.filter(id => id !== null);
    
    setSaving(true);
    try {
      // Call the API to push selected cases to backoffice
      // This will update both the lead status and case statuses in one call
      const response = await agentApi.pushToBackoffice(leadId, {
        case_ids: validCaseIds
      });
      
      setSuccessAlert({
        show: true,
        message: 'Selected cases have been successfully pushed to backoffice!'
      });
      
      console.log('Push to backoffice response:', response.data);
      
      // Refresh cases to show updated status
      await fetchLeadCases();
      
      // Refresh lead details to show updated status
      await fetchLead();
      
    } catch (err) {
      console.error('Error pushing to backoffice:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.detail || 'Failed to push cases to backoffice. Please try again.',
        confirmButtonColor: '#343a40'
      });
    } finally {
      setSaving(false);
    }
  };

  // Effect to fetch permanent cities when permanent state is selected
  useEffect(() => {
    if (lead?.permanent_state_id && !lead?.perma_address_same) {
      fetchPermanentCities(lead.permanent_state_id);
    }
  }, [lead?.permanent_state_id, lead?.perma_address_same]);
  
  // Effect to fetch permanent pincodes when permanent city is selected
  useEffect(() => {
    if (lead?.permanent_city_id && !lead?.perma_address_same) {
      fetchPermanentPincodes(lead.permanent_city_id);
    }
  }, [lead?.permanent_city_id, lead?.perma_address_same]);

  const handleInputChange = (e, selectInfo) => {
    let name, value, type, checked;

    if (selectInfo && selectInfo.name) {
      // Handle react-select change
      name = selectInfo.name;
      value = e ? e.value : null; // e is the selected option { value, label } or null
    } else if (e && e.target) {
      // Handle standard input change
      name = e.target.name;
      value = e.target.value;
      type = e.target.type;
      checked = e.target.checked;
    } else {
      // If event is somehow null or unexpected, do nothing
      return;
    }

    setLead(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      // Reset dependent fields when a parent changes
      ...(name === 'state_id' && { city_id: null, pincode_id: null }), // Use null for react-select
      ...(name === 'city_id' && { pincode_id: null }), // Use null for react-select
      ...(name === 'office_state_id' && { office_city_id: null, office_pincode: '' }), // Reset office city (select) and pincode (text)
      ...(name === 'office_city_id' && { office_pincode: '' }), // Reset office pincode text
      ...(name === 'permanent_state_id' && { permanent_city_id: null, permanent_pincode_id: null }), // Use null for react-select
      ...(name === 'permanent_city_id' && { permanent_pincode_id: null }), // Use null for react-select
      // For perma_address_same checkbox, we need special handling
      ...(name === 'perma_address_same' && checked && {
        permanent_address: prev.address,
        permanent_state_id: prev.state_id,
        permanent_city_id: prev.city_id,
        permanent_pincode_id: prev.pincode_id,
        permanent_residence_type: prev.residence_type
      }),
    }));

    // Trigger dependent fetches for react-select changes
    if (selectInfo && selectInfo.name) {
        if (name === 'state_id') {
            setCities([]); // Clear cities immediately
            setPincodes([]); // Clear pincodes immediately
            if (value) fetchCities(value);
        }
        if (name === 'city_id') {
            setPincodes([]); // Clear pincodes immediately
            if (value) fetchPincodes(value);
        }
        if (name === 'office_state_id') {
            setOfficeCities([]); // Clear office cities immediately
            if (value) fetchOfficeCities(value);
        }
        if (name === 'office_city_id') {
            setOfficePincodes([]); // Clear office pincodes immediately
            if (value) fetchOfficePincodes(value);
        }
        if (name === 'permanent_state_id') {
            setPermanentCities([]); // Clear permanent cities immediately
            setPermanentPincodes([]); // Clear permanent pincodes immediately
            if (value) fetchPermanentCities(value);
        }
        if (name === 'permanent_city_id') {
            setPermanentPincodes([]); // Clear permanent pincodes immediately
            if (value) fetchPermanentPincodes(value);
        }
    }
  };

  const handleSubmit = async (e, section) => {
    e.preventDefault();
    
    // Prevent saving if lead status is 7 or higher
    if (lead?.lead_status >= 7) {
      Swal.fire({
        icon: 'warning',
        title: 'Lead Locked',
        text: 'This lead has been pushed to backoffice and cannot be edited.',
        confirmButtonColor: '#343a40'
      });
      return;
    }
    
    setSaving(true);
    setSaveError('');
    
    try {
      const updatedLead = { ...lead };
      
      // Update status based on the section, but only if current status is lower
      switch (section) {
        case 'personal':
          // Only set to 3 if current status is 3 or below
          if (lead.lead_status <= 3) {
            updatedLead.lead_status = 3; // Personal details
          }
          break;
        case 'location':
          // Only set to 4 if current status is 4 or below
          if (lead.lead_status <= 4) {
            updatedLead.lead_status = 4; // Residential details
          }
          break;
        case 'income':
          // Only set to 5 if current status is 5 or below
          if (lead.lead_status <= 5) {
            updatedLead.lead_status = 5; // Professional details
          }
          break;
        case 'documents':
          // Only set to 6 if current status is 6 or below
          if (lead.lead_status <= 6) {
            updatedLead.lead_status = 6; // Documents uploaded
          }
          break;
        case 'application':
          // Application tab always sets to 7 if not already higher
          if (lead.lead_status < 7) {
            updatedLead.lead_status = 7; // Application details
          }
          break;
      }
      
      // Ensure property fields are included for relevant categories
      if (updatedLead.case_type === "Home Loan" || updatedLead.case_type === "LAP") {
        // Make sure property fields are explicitly included in the payload
        updatedLead.property_identified = updatedLead.property_identified || "No";
        // Only include these fields if property is identified
        if (updatedLead.property_identified === "Yes") {
          updatedLead.property_value = updatedLead.property_value || 0;
          updatedLead.property_city = updatedLead.property_city || "";
          updatedLead.total_monthly_emi = updatedLead.total_monthly_emi || 0;
        }
        
        console.log("Sending property details to API:", {
          property_identified: updatedLead.property_identified,
          property_value: updatedLead.property_value,
          property_city: updatedLead.property_city,
          total_monthly_emi: updatedLead.total_monthly_emi
        });
      }
      
      // Save to backend
      await agentApi.updateLead(leadId, updatedLead);
      
      // Update local state with the new lead data including status
      setLead(updatedLead);
      
      // Show success message
      setSuccessAlert({
        show: true,
        message: 'Lead details saved successfully!'
      });
      
      // Auto-navigate to next tab after saving
      let nextTab = section;
      if (section === 'personal') {
        nextTab = 'location';
      } else if (section === 'location') {
        nextTab = 'income';
      } else if (section === 'income') {
        nextTab = 'documents';
      } else if (section === 'documents') {
        nextTab = 'application';
      }
      
      // Set active tab with a slight delay to ensure React state updates properly
      setTimeout(() => {
        setActiveTab(nextTab);
      }, 10);
      
      // If we just saved to status 7, fetch the lead data again
      if (section === 'application') {
        await fetchLead();
      }
      
    } catch (err) {
      console.error('Error saving lead:', err);
      setSaveError(err.response?.data?.detail || 'Failed to save lead details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    navigate('/agent/leads');
  };
  
  // Notification handlers
  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
  };
  
  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    // Only proceed if the notification exists and is unread
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      // Decrement count optimistically ONLY if it was unread
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  
  const handleMarkAllAsRead = async () => {
    // Only proceed if there are unread notifications
    if (unreadCount === 0) return;

    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };
  
  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) { return timestamp; } // Fallback
  };
  
  // Search handlers
  const performSearch = async (query) => {
    if (!query.trim() || query.length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await searchApi.globalSearch(query, { limit_per_type: 5 });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
  };
  
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter' && searchQuery.trim().length > 1) {
      performSearch(searchQuery);
      // Show modal for mobile view
      if (window.innerWidth < 768) {
        setShowSearchModal(true);
      }
    }
  };
  
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };
  
  const handleSearchResultClick = () => {
    setShowSearchResults(false);
    setShowSearchModal(false);
  };
  
  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  // Helper function to get case status name
  const getCaseStatusName = (statusCode) => {
    switch(Number(statusCode)) {
      case 1: return "Requested";
      case 2: return "Approved";
      case 3: return "Disbursed";
      case 4: return "Rejected";
      case 7: return "Pushed to Backoffice";
      case 10: return "Processing";
      case 13: return "Eligible for Payout";
      default: return "Unknown";
    }
  };

  // Helper function to get case type name
  const getCaseTypeName = (typeCode) => {
    return typeCode
  };

  // Helper function to get status badge variant
  const getStatusBadgeVariant = (statusCode) => {
    switch(Number(statusCode)) {
      case 1: return "warning";  // Requested
      case 2: return "success";  // Approved
      case 3: return "info";     // Disbursed
      case 4: return "danger";   // Rejected
      case 7: return "info";     // Pushed to Backoffice
      case 10: return "secondary"; // Processing
      case 13: return "success";   // Eligible for Payout
      case 11: return "danger";    // Failed
      default: return "primary";
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Prevent document upload if lead status is 7 or higher
    if (lead?.lead_status >= 7) {
      Swal.fire({
        icon: 'warning',
        title: 'Lead Locked',
        text: 'This lead has been pushed to backoffice and cannot be edited.',
        confirmButtonColor: '#343a40'
      });
      // Clear the file input
      e.target.value = null;
      return;
    }
    
    const uploadKey = docType;
    
    setUploading(prev => ({ ...prev, [uploadKey]: true }));
    setUploadError(prev => ({ ...prev, [uploadKey]: null }));

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      // Map from frontend doc type to backend doc type
      const documentTypeMap = {
        'aadhaar': 'adhaar_card_file',
        'pan': 'pan_card_file',
        'addressProof': 'voter_id_card_file',
        'bankStatements': 'bank_statements_file',
        'bankSoa': 'bank_soa_file',
        'form16': 'form_16_file',
        'officeId': 'office_id_card_file',
        'propertyAtsTitle': 'property_ats_title_file',
        'loanDeclarationSheet': 'loan_declaration_sheet_file',
        'otherPropertyDocs': 'additional_documents'
      };

      const backendDocumentType = documentTypeMap[docType];
      
      if (!backendDocumentType) {
        throw new Error(`Unsupported document type: ${docType}`);
      }
      
      // Use the correct uploadLeadDocument method and append document_type to formData
      uploadFormData.append('document_type', backendDocumentType);
      
      console.log(`Uploading ${docType}`, backendDocumentType);
      
      // Call the API with the correct method and parameters
      const response = await agentApi.uploadLeadDocument(leadId, uploadFormData);

      const { url } = response.data; // URL comes from the response

      // Update documents state to show the uploaded file name
      const newDocState = { ...documents };
      newDocState[backendDocumentType] = { name: file.name, url };
      setDocuments(newDocState);

      console.log("Updated documents state:", newDocState);

      setSuccessAlert({
        show: true,
        message: 'Document uploaded successfully!'
      });
      
    } catch (error) {
      console.error(`Error uploading ${uploadKey}:`, error);
      const errorMsg = error.response?.data?.detail || `Failed to upload ${file.name}. Please try again.`;
      setUploadError(prev => ({ ...prev, [uploadKey]: errorMsg }));
      
      // Show error message
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: errorMsg,
        confirmButtonColor: '#343a40'
      });
      
      // Clear the file input if needed
      e.target.value = null; 
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleLoanDocumentUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [docType]: true }));
    setUploadError(prev => ({ ...prev, [docType]: null }));

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);
    formData.append('business_type', 'none');

    // For partner/director documents, add additional info
    if (docType === 'partners_documents' || docType === 'directors_documents') {
      const additionalInfo = JSON.stringify({
        index: 0, // Default to first partner/director
        doc_type: 'kyc_file',
        name: 'Partner/Director 1'
      });
      formData.append('additional_info', additionalInfo);
    }

    try {
      await dsoApi.uploadLoanDocument(leadId, formData);
      // Refresh documents
      fetchDocuments();
      fetchLoanDocuments();
    } catch (error) {
      console.error(`Error uploading ${docType}:`, error);
      setUploadError(prev => ({ 
        ...prev, 
        [docType]: error.response?.data?.detail || `Failed to upload ${file.name}`
      }));
    } finally {
      setUploading(prev => ({ ...prev, [docType]: false }));
    }
  };

  const uploadDocumentAndRefresh = async (e, docType) => {
    await handleDocumentUpload(e, docType);
    // After upload is complete, fetch documents again to refresh the UI
    fetchDocuments();
    fetchLoanDocuments();
  };

  const uploadLoanDocumentAndRefresh = async (e, docType) => {
    await handleLoanDocumentUpload(e, docType);
    // After upload is complete, fetch documents again to refresh the UI
    fetchDocuments();
    fetchLoanDocuments();
  };


  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <div className="agent-dashboard-layout">
      <AgentSidebar />
      <div className="agent-content-wrapper">
        <header className="agent-header">
          <div className="agent-greeting">
            <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
            {lead?.lead_status >= 7 && (
              <span className="ms-3 text-warning">
                <FaInfoCircle className="me-1" /> Lead has been pushed to backoffice. Most fields are locked for editing.
              </span>
            )}
          </div>

          <div ref={searchRef} className="agent-header-search">
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search leads, products, invoices..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {searchQuery && (
                <button className="clear-search-btn" onClick={clearSearch}>
                  <FaTimes />
                </button>
              )}
              {isSearching && (
                <Spinner 
                  animation="border" 
                  size="sm" 
                  className="search-spinner" 
                />
              )}
            </div>
            
            {/* Search results dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-dropdown">
                <SearchResults 
                  results={searchResults}
                  onResultClick={handleSearchResultClick}
                />
              </div>
            )}
          </div>

          <div className="agent-header-actions">
            <div className="agent-header-icon">
              <FaQuestionCircle size={18} />
            </div>
            
            <div ref={notificationRef} className="position-relative agent-notifications me-3">
              <Button
                variant="link"
                className="text-secondary p-0 position-relative"
                onClick={toggleNotifications}
                id="dropdown-notifications-agent"
                aria-expanded={showNotifications}
                title="Notifications"
              >
                <FaBell size={20} />
                {unreadCount > 0 && (
                  <Badge
                    pill
                    bg="danger"
                    className="position-absolute top-0 start-100 translate-middle border border-light"
                    style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                    <span className="visually-hidden">unread notifications</span>
                  </Badge>
                )}
              </Button>

              {/* Notification Dropdown Menu */}
              <Dropdown.Menu
                show={showNotifications}
                className="shadow mt-2 notification-dropdown"
                style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }}
              >
                <Dropdown.Header className="d-flex justify-content-between align-items-center">
                  Notifications
                  {unreadCount > 0 && (
                    <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                      Mark all read
                    </Button>
                  )}
                </Dropdown.Header>
                <Dropdown.Divider className="my-1" />
                {loadingNotifications ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" variant="primary" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  <ListGroup variant="flush">
                    {notifications.map(notif => (
                      <ListGroup.Item
                        key={notif.id}
                        action={!notif.is_read}
                        onClick={() => handleMarkAsRead(notif.id)}
                        style={{
                          cursor: !notif.is_read ? 'pointer' : 'default',
                          backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent',
                          borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none',
                          padding: '0.75rem 1.25rem',
                          borderBottom: '1px solid #eee'
                        }}
                        className="notification-item"
                      >
                        <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                        <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                        <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <Dropdown.ItemText className="text-center text-muted p-3">
                    You're all caught up!
                  </Dropdown.ItemText>
                )}
              </Dropdown.Menu>
            </div>

            <div className="agent-user-profile">
              <div className="agent-avatar">
                <FaUser />
              </div>
              <div className="agent-user-info">
                <span className="agent-user-name">
                  {currentUser?.name || 'Agent User'}
                </span>
                <button className="agent-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </header>
        
        <div className="agent-main-content">
          <Container fluid className="edit-lead-container">
            {successAlert.show && (
              <Alert variant="success" onClose={() => setSuccessAlert({ show: false, message: '' })} dismissible>
                {successAlert.message}
              </Alert>
            )}
            
            <Tab.Container id="lead-tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <div className="edit-lead-content">
          <Nav variant="tabs" className="edit-lead-tabs mb-4">
            <Nav.Item>
              <Nav.Link 
                eventKey="personal" 
                className="edit-lead-tab"
                disabled={lead?.lead_status >= 7}
                onClick={(e) => {
                  if (lead?.lead_status >= 7) {
                    e.preventDefault();
                  }
                }}
              >
                <FaUser className="tab-icon" /> Personal Information
                {lead?.lead_status >= 7 && <FaLock size="12" className="ms-1" />}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                eventKey="location" 
                className="edit-lead-tab"
                disabled={lead?.lead_status >= 7}
                onClick={(e) => {
                  if (lead?.lead_status >= 7) {
                    e.preventDefault();
                  }
                }}
              >
                <FaMapMarkerAlt className="tab-icon" /> Location
                {lead?.lead_status >= 7 && <FaLock size="12" className="ms-1" />}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                eventKey="income" 
                className="edit-lead-tab"
                disabled={lead?.lead_status >= 7}
                onClick={(e) => {
                  if (lead?.lead_status >= 7) {
                    e.preventDefault();
                  }
                }}
              >
                <FaBriefcase className="tab-icon" /> Income & Employment
                {lead?.lead_status >= 7 && <FaLock size="12" className="ms-1" />}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                eventKey="documents" 
                className="edit-lead-tab"
                disabled={lead?.lead_status >= 7}
                onClick={(e) => {
                  if (lead?.lead_status >= 7) {
                    e.preventDefault();
                  }
                }}
              >
                <FaFileAlt className="tab-icon" /> Documents
                {lead?.lead_status >= 7 && <FaLock size="12" className="ms-1" />}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="application" className="edit-lead-tab">
                <FaFileInvoiceDollar className="tab-icon" /> Application Details
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Form onSubmit={(e) => e.preventDefault()}>
            <Tab.Content>
              {/* Personal Information Tab */}
              <Tab.Pane eventKey="personal">
                <Card className="tab-card">
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Full Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={lead?.name || ''}
                            onChange={handleInputChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email Address</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={lead?.email || ''}
                            readOnly
                            disabled
                            className="bg-light"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Lead Status</Form.Label>
                          <Form.Control
                            type="text"
                            value={leadStatuses.find(status => status.id === lead?.lead_status)?.name || 'New Lead'}
                            readOnly
                            disabled
                            className="bg-light"
                          />
                        </Form.Group>
                      </Col>
                      {/* You can add another field here if needed, or adjust Col size */}
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Mobile Number</Form.Label>
                          <Form.Control
                            type="tel"
                            name="mobile"
                            value={lead?.mobile || ''}
                            readOnly
                            disabled
                            className="bg-light"
                            maxLength={10}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Date of Birth</Form.Label>
                          <Form.Control
                            type="date"
                            name="date_of_birth"
                            value={lead?.date_of_birth?.split('T')[0] || ''}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Gender</Form.Label>
                          <Select
                            name="gender"
                            options={[
                              { value: "MALE", label: "Male" },
                              { value: "FEMALE", label: "Female" }
                            ]}
                            value={[
                              { value: "MALE", label: "Male" },
                              { value: "FEMALE", label: "Female" }
                            ].find(option => option.value === lead?.gender) || null}
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'gender' })}
                            placeholder="Select Gender"
                            isClearable
                            inputId="gender"
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            styles={{
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              menu: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              control: base => ({
                                ...base,
                                zIndex: 100
                              })
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Mother's Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="mothers_name"
                            value={lead?.mothers_name || ''}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Category</Form.Label>
                          <Select
                            name="case_type"
                            options={Object.entries(caseTypeWithIcons).map(([label, { icon }]) => ({
                              value: label,
                              label: label,
                              icon: icon
                            }))}
                            value={lead?.case_type ? {
                              value: lead.case_type,
                              label: lead.case_type,
                              icon: caseTypeWithIcons[lead.case_type]?.icon
                            } : null}
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'case_type' })}
                            placeholder="Select Category"
                            isClearable
                            formatOptionLabel={option => (
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                {option.icon}
                                <span>{option.label}</span>
                              </div>
                            )}
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            styles={{
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              menu: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              control: base => ({
                                ...base,
                                zIndex: 100
                              })
                            }}
                          />
                        </Form.Group>
                        
                        {/* Property Details for Home Loan and LAP categories */}
                        {(lead?.case_type === "Home Loan" || lead?.case_type === "LAP") && (
                          <>
                            <Col md={12}>
                              <hr className="mt-2 mb-3" />
                              <h5>Property Details</h5>
                            </Col>
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>Property Identified</Form.Label>
                                <div>
                                  <Form.Check
                                    inline
                                    type="radio"
                                    id="property-yes"
                                    name="property_identified"
                                    label="Yes"
                                    checked={lead?.property_identified === "Yes"}
                                    onChange={(e) => handleInputChange({target: {name: 'property_identified', value: 'Yes'}}, null)}
                                  />
                                  <Form.Check
                                    inline
                                    type="radio"
                                    id="property-no"
                                    name="property_identified"
                                    label="No"
                                    checked={lead?.property_identified === "No" || !lead?.property_identified}
                                    onChange={(e) => handleInputChange({target: {name: 'property_identified', value: 'No'}}, null)}
                                  />
                                </div>
                              </Form.Group>
                            </Col>
                            
                            {lead?.property_identified === "Yes" && (
                              <>
                                <Form.Group className="mb-3">
                                  <Form.Label>Property Value</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="property_value"
                                    value={lead?.property_value || ''}
                                    onChange={handleInputChange}
                                    placeholder="Enter property value"
                                    min="0"
                                    step="any"
                                  />
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                  <Form.Label>Property City</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="property_city"
                                    value={lead?.property_city || ''}
                                    onChange={handleInputChange}
                                    placeholder="Enter property city"
                                  />
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                  <Form.Label>Total Monthly EMI</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="total_monthly_emi"
                                    value={lead?.total_monthly_emi || ''}
                                    onChange={handleInputChange}
                                    placeholder="Enter total monthly EMI"
                                    min="0"
                                    step="any"
                                  />
                                </Form.Group>
                              </>
                            )}
                          </>
                        )}
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Person Type</Form.Label>
                          <Select
                            name="person_type"
                            options={[
                              { value: "Individual", label: "Individual" },
                              { value: "Business", label: "Business" }
                            ]}
                            value={[
                              { value: "Individual", label: "Individual" },
                              { value: "Business", label: "Business" }
                            ].find(option => option.value === lead?.person_type) || null}
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'person_type' })}
                            placeholder="Select Person Type"
                            isClearable
                            inputId="person_type"
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            styles={{
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              menu: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              control: base => ({
                                ...base,
                                zIndex: 100
                              })
                            }}
                          />
                        </Form.Group>
                      </Col>
                      
                      {/* Business Type - conditionally shown when Person Type is Business */}
                      {lead?.person_type === "Business" && (
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Business Type</Form.Label>
                            <Select
                              name="business_type"
                              options={[
                                { value: "Proprietorship", label: "Proprietorship" },
                                { value: "Partnership Firm", label: "Partnership Firm" },
                                { value: "Pvt Ltd Company", label: "Pvt Ltd Company" }
                              ]}
                              value={[
                                { value: "Proprietorship", label: "Proprietorship" },
                                { value: "Partnership Firm", label: "Partnership Firm" },
                                { value: "Pvt Ltd Company", label: "Pvt Ltd Company" }
                              ].find(option => option.value === lead?.business_type) || null}
                              onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'business_type' })}
                              placeholder="Select Business Type"
                              isClearable
                              inputId="business_type"
                              menuPortalTarget={document.body}
                              menuPosition={'fixed'}
                              styles={{
                                menuPortal: base => ({
                                  ...base,
                                  zIndex: 9999999
                                }),
                                menu: base => ({
                                  ...base,
                                  zIndex: 9999999
                                }),
                                control: base => ({
                                  ...base,
                                  zIndex: 100
                                })
                              }}
                            />
                          </Form.Group>
                        </Col>
                      )}
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Religion</Form.Label>
                          <Select
                            name="religion"
                            options={[
                              { value: "Hindu", label: "Hindu" },
                              { value: "Muslim", label: "Muslim" },
                              { value: "Sikh", label: "Sikh" },
                              { value: "Christian", label: "Christian" },
                              { value: "Buddhism", label: "Buddhism" },
                              { value: "Jain", label: "Jain" },
                              { value: "Parsi", label: "Parsi" }
                            ]}
                            value={[
                              { value: "Hindu", label: "Hindu" },
                              { value: "Muslim", label: "Muslim" },
                              { value: "Sikh", label: "Sikh" },
                              { value: "Christian", label: "Christian" },
                              { value: "Buddhism", label: "Buddhism" },
                              { value: "Jain", label: "Jain" },
                              { value: "Parsi", label: "Parsi" }
                            ].find(option => option.value === lead?.religion) || null}
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'religion' })}
                            placeholder="Select Religion"
                            isClearable
                            inputId="religion"
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            styles={{
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              menu: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              control: base => ({
                                ...base,
                                zIndex: 100
                              })
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Marital Status</Form.Label>
                          <Select
                            name="marital_status"
                            options={[
                              { value: "Single", label: "Single" },
                              { value: "Married", label: "Married" },
                              { value: "Divorced", label: "Divorced" },
                              { value: "Widowed", label: "Widowed" }
                            ]}
                            value={[
                              { value: "Single", label: "Single" },
                              { value: "Married", label: "Married" },
                              { value: "Divorced", label: "Divorced" },
                              { value: "Widowed", label: "Widowed" }
                            ].find(option => option.value === lead?.marital_status) || null}
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'marital_status' })}
                            placeholder="Select Marital Status"
                            isClearable
                            inputId="marital_status"
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            styles={{
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              menu: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              control: base => ({
                                ...base,
                                zIndex: 100
                              })
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Educational Qualification</Form.Label>
                          <Select
                            name="educational_qualification"
                            options={[
                              { value: "High School", label: "High School" },
                              { value: "Bachelor's Degree", label: "Bachelor's Degree" },
                              { value: "Master's Degree", label: "Master's Degree" },
                              { value: "PhD", label: "PhD" },
                              { value: "Diploma", label: "Diploma" },
                              { value: "Others", label: "Others" }
                            ]}
                            value={[
                              { value: "High School", label: "High School" },
                              { value: "Bachelor's Degree", label: "Bachelor's Degree" },
                              { value: "Master's Degree", label: "Master's Degree" },
                              { value: "PhD", label: "PhD" },
                              { value: "Diploma", label: "Diploma" },
                              { value: "Others", label: "Others" }
                            ].find(option => option.value === lead?.educational_qualification) || null}
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'educational_qualification' })}
                            placeholder="Select Educational Qualification"
                            isClearable
                            inputId="educational_qualification"
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            styles={{
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              menu: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              control: base => ({
                                ...base,
                                zIndex: 100
                              })
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Residence Type</Form.Label>
                          <Select
                            name="residence_type"
                            options={[
                              { value: "Owned", label: "Owned" },
                              { value: "Rented", label: "Rented" },
                              { value: "Company Provided", label: "Company Provided" },
                              { value: "Parental", label: "Parental" }
                            ]}
                            value={[
                              { value: "Owned", label: "Owned" },
                              { value: "Rented", label: "Rented" },
                              { value: "Company Provided", label: "Company Provided" },
                              { value: "Parental", label: "Parental" }
                            ].find(option => option.value === lead?.residence_type) || null}
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'residence_type' })}
                            placeholder="Select Residence Type"
                            isClearable
                            inputId="residence_type"
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            styles={{
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              menu: base => ({
                                ...base,
                                zIndex: 9999999
                              }),
                              control: base => ({
                                ...base,
                                zIndex: 100
                              })
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row className="mb-4 mt-3">
                      <Col md={6} className="d-flex align-items-center">
                        {/* Lead Status Badge */}
                        {lead && (
                          <div className="lead-status-badge-container mb-0 ps-0">
                            <span className='me-2 fw-bold'>Lead status:</span>
                            <div className={`lead-status-badge status-${lead.lead_status || 1}`}>
                              {lead.lead_status === 1 && <FaUser className="me-2" />}
                              {lead.lead_status === 2 && <FaCreditCard className="me-2" />}
                              {lead.lead_status === 3 && <FaUser className="me-2" />}
                              {lead.lead_status === 4 && <FaMapMarkerAlt className="me-2" />}
                              {lead.lead_status === 5 && <FaBriefcase className="me-2" />}
                              {lead.lead_status === 6 && <FaFileAlt className="me-2" />}
                              {lead.lead_status === 7 && <FaCheckCircle className="me-2" />}
                              {lead.lead_status >= 8 && lead.lead_status <= 10 && <FaFileInvoiceDollar className="me-2" />}
                              {lead.lead_status === 11 && <FaTimesCircle className="me-2" />}
                              {lead.lead_status >= 12 && <FaCheckCircle className="me-2" />}
                              {leadStatuses.find(status => status.id === lead.lead_status)?.name || "New Lead"}
                            </div>
                          </div>
                        )}
                      </Col>
                      <Col md={6} className="d-flex justify-content-end align-items-center">
                        <Button 
                          variant="primary" 
                          disabled={saving || lead?.lead_status >= 7}
                          onClick={(e) => {
                            e.preventDefault(); // Prevent default form submission
                            handleSubmit(e, 'personal');
                          }}
                        >
                          {saving ? (
                            <><FaSave className="me-2" /> Saving...</>
                          ) : lead?.lead_status >= 7 ? (
                            <><FaLock className="me-2" /> Locked</>
                          ) : (
                            <><FaSave className="me-2" /> Save & Continue</>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Location Tab */}
              <Tab.Pane eventKey="location">
                <Card className="tab-card">
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Address Type</Form.Label>
                          <Select
                            name="address_type"
                            options={[
                              { value: 'Residential', label: 'Residential' },
                              { value: 'Office', label: 'Office' },
                            ]}
                            value={
                              lead?.address_type
                                ? { value: lead.address_type, label: lead.address_type }
                                : { value: 'Residential', label: 'Residential' } // Default to Residential
                            }
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'address_type' })}
                            placeholder="Select Address Type"
                            isClearable={false}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Address</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="address"
                            value={lead?.address || ''}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>State</Form.Label>
                          <Select
                            name="state_id"
                            options={states.map(state => ({ value: state.id, label: state.name }))}
                            value={states.map(s => ({ value: s.id, label: s.name })).find(s => s.value === lead?.state_id) || null}
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'state_id' })}
                            placeholder="Select State"
                            isLoading={loadingLocations && !states.length} // Show loading initially
                            isClearable
                            inputId="residential_state_id"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>City</Form.Label>
                          <Select
                            name="city_id"
                            options={cities.map(city => ({ value: city.id, label: city.name }))}
                            value={cities.map(c => ({ value: c.id, label: c.name })).find(c => c.value === lead?.city_id) || null}
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'city_id' })}
                            placeholder="Select City"
                            isDisabled={!lead?.state_id || (loadingLocations && !states.length)} // Disable if no state or states still loading
                            isLoading={loadingLocations && !!lead?.state_id && !cities.length} // Show loading only when fetching cities for a selected state
                            isClearable
                            inputId="residential_city_id"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Pincode</Form.Label>
                          <Select
                            name="pincode_id"
                            options={pincodes.map(pincode => ({ value: pincode.id, label: pincode.code }))}
                            value={pincodes.map(p => ({ value: p.id, label: p.code })).find(p => p.value === lead?.pincode_id) || null}
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'pincode_id' })}
                            placeholder="Select Pincode"
                            isDisabled={!lead?.city_id || (loadingLocations && (!states.length || !cities.length))} // Disable if no city or parent data still loading
                            isLoading={loadingLocations && !!lead?.city_id && !pincodes.length} // Show loading only when fetching pincodes for a selected city
                            isClearable
                            inputId="residential_pincode_id"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Form.Group className="mb-3">
                          <div 
                            className="d-flex align-items-center" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              // Toggle the checkbox state when clicking anywhere in the container
                              const newChecked = !(lead?.perma_address_same || false);
                              setLead(prev => ({
                                ...prev,
                                perma_address_same: newChecked,
                                // If checked, copy residential address to permanent address
                                ...(newChecked)
                              }));
                              
                              
                            }}
                          >
                            <Form.Check
                              type="checkbox"
                              name="perma_address_same"
                              label=""
                              checked={lead?.perma_address_same || false}
                              onChange={(e) => {
                                // This will still handle direct checkbox clicks
                                const { checked } = e.target;
                                setLead(prev => ({
                                  ...prev,
                                  perma_address_same: checked,
                                  // If checked, copy residential address to permanent address
                                  ...(checked)
                                }));
                                
                                
                              }}
                              style={{ marginRight: '8px' }}
                              onClick={(e) => e.stopPropagation()} // Prevent duplicate triggers
                            />
                            <span className="form-check-label">Permanent Address same as Residential Address</span>
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
                
                {/* Permanent Address Section - conditionally rendered when perma_address_same is false */}
                {!lead?.perma_address_same && (
                  <Card className="tab-card mt-4">
                    <Card.Body>
                      <h3><FaHome className="me-2 section-icon" /> Permanent Address</h3>
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Permanent Address</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              name="permanent_address"
                              value={lead?.permanent_address || ''}
                              onChange={handleInputChange}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>State</Form.Label>
                            <Select
                              name="permanent_state_id"
                              options={states.map(state => ({ value: state.id, label: state.name }))}
                              value={states.map(s => ({ value: s.id, label: s.name })).find(s => s.value === lead?.permanent_state_id) || null}
                              onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'permanent_state_id' })}
                              placeholder="Select State"
                              isLoading={loadingLocations && !states.length}
                              isClearable
                              inputId="permanent_state_id"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>City</Form.Label>
                            <Select
                              name="permanent_city_id"
                              options={permanentCities.map(city => ({ value: city.id, label: city.name }))}
                              value={permanentCities.map(c => ({ value: c.id, label: c.name })).find(c => c.value === lead?.permanent_city_id) || null}
                              onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'permanent_city_id' })}
                              placeholder="Select City"
                              isDisabled={!lead?.permanent_state_id}
                              isLoading={loadingLocations && !!lead?.permanent_state_id && !permanentCities.length}
                              isClearable
                              inputId="permanent_city_id"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Pincode</Form.Label>
                            <Select
                              name="permanent_pincode_id"
                              options={permanentPincodes.map(pincode => ({ value: pincode.id, label: pincode.code }))}
                              value={permanentPincodes.map(p => ({ value: p.id, label: p.code })).find(p => p.value === lead?.permanent_pincode_id) || null}
                              onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'permanent_pincode_id' })}
                              placeholder="Select Pincode"
                              isDisabled={!lead?.permanent_city_id}
                              isLoading={loadingLocations && !!lead?.permanent_city_id && !permanentPincodes.length}
                              isClearable
                              inputId="permanent_pincode_id"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Residence Type</Form.Label>
                            <Select
                              name="permanent_residence_type"
                              options={[
                                { value: 'Owned', label: 'Owned' },
                                { value: 'Rented', label: 'Rented' },
                                { value: 'Parental', label: 'Parental' },
                                { value: 'Company Provided', label: 'Company Provided' },
                                { value: 'PG/Hostel', label: 'PG/Hostel' },
                                { value: 'Other', label: 'Other' },
                              ]}
                              value={
                                lead?.permanent_residence_type
                                  ? { value: lead.permanent_residence_type, label: lead.permanent_residence_type }
                                  : null
                              }
                              onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'permanent_residence_type' })}
                              placeholder="Select Residence Type"
                              isClearable
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}
                <Row className="mb-4 mt-3">
                  <Col md={6} className="d-flex align-items-center">
                    {/* Lead Status Badge */}
                    {lead && (
                      <div className="lead-status-badge-container mb-0 ps-0">
                        <span className='me-2 fw-bold'>Lead status:</span>
                        <div className={`lead-status-badge status-${lead.lead_status || 1}`}>
                          {lead.lead_status === 1 && <FaUser className="me-2" />}
                          {lead.lead_status === 2 && <FaCreditCard className="me-2" />}
                          {lead.lead_status === 3 && <FaUser className="me-2" />}
                          {lead.lead_status === 4 && <FaMapMarkerAlt className="me-2" />}
                          {lead.lead_status === 5 && <FaBriefcase className="me-2" />}
                          {lead.lead_status === 6 && <FaFileAlt className="me-2" />}
                          {lead.lead_status === 7 && <FaCheckCircle className="me-2" />}
                          {lead.lead_status >= 8 && lead.lead_status <= 10 && <FaFileInvoiceDollar className="me-2" />}
                          {lead.lead_status === 11 && <FaTimesCircle className="me-2" />}
                          {lead.lead_status >= 12 && <FaCheckCircle className="me-2" />}
                          {leadStatuses.find(status => status.id === lead.lead_status)?.name || "New Lead"}
                        </div>
                      </div>
                    )}
                  </Col>
                  <Col md={6} className="d-flex justify-content-end align-items-center">
                    <Button 
                      variant="primary" 
                      disabled={saving || lead?.lead_status >= 7}
                      onClick={(e) => {
                        e.preventDefault(); // Prevent default form submission
                        handleSubmit(e, 'location');
                      }}
                    >
                      {saving ? (
                        <><FaSave className="me-2" /> Saving...</>
                      ) : lead?.lead_status >= 7 ? (
                        <><FaLock className="me-2" /> Locked</>
                      ) : (
                        <><FaSave className="me-2" /> Save & Continue</>
                      )}
                    </Button>
                  </Col>
                </Row>
              </Tab.Pane>

              {/* Income & Employment Tab */}
              <Tab.Pane eventKey="income">
                <Card className="tab-card">
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Profession</Form.Label>
                          <Select
                            name="profession"
                            options={[
                              { value: 'Software Engineer', label: 'Software Engineer' },
                              { value: 'Doctor', label: 'Doctor' },
                              { value: 'Lawyer', label: 'Lawyer' },
                              { value: 'Teacher', label: 'Teacher' },
                              { value: 'Accountant', label: 'Accountant' },
                              { value: 'Others', label: 'Others' }
                            ]}
                            value={
                              lead?.profession
                                ? { value: lead.profession, label: lead.profession }
                                : null
                            }
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'profession' })}
                            placeholder="Select Profession"
                            isClearable
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Employment Type</Form.Label>
                          <Select
                            name="employer_type"
                            options={[
                              { value: 'Salaried', label: 'Salaried' },
                              { value: 'Self Employed', label: 'Self Employed' }
                            ]}
                            value={
                              lead?.employer_type
                                ? { value: lead.employer_type, label: lead.employer_type }
                                : null
                            }
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'employer_type' })}
                            placeholder="Select Employment Type"
                            isClearable
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Employer Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="employer_name"
                            value={lead?.employer_name || ''}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Monthly Income (In Hand)</Form.Label>
                          <Form.Control
                            type="text"
                            name="monthly_inhand"
                            value={lead?.monthly_inhand || ''}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Designation</Form.Label>
                          <Select
                            name="designation"
                            options={[
                              { value: 'Software Developer', label: 'Software Developer' },
                              { value: 'Project Manager', label: 'Project Manager' },
                              { value: 'Business Analyst', label: 'Business Analyst' },
                              { value: 'HR Manager', label: 'HR Manager' },
                              { value: 'Team Lead', label: 'Team Lead' },
                              { value: 'Others', label: 'Others' }
                            ]}
                            value={
                              lead?.designation
                                ? { value: lead.designation, label: lead.designation }
                                : null
                            }
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'designation' })}
                            placeholder="Select Designation"
                            isClearable
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Total Work Experience</Form.Label>
                          <Select
                            name="total_work_experience"
                            options={[
                              { value: "0", label: "0 years" },
                              { value: "1", label: "1 year" },
                              { value: "2", label: "2 years" },
                              { value: "3", label: "3 years" },
                              { value: "4", label: "4 years" },
                              { value: "5", label: "5 years" },
                              { value: "6", label: "6 years" },
                              { value: "7", label: "7 years" },
                              { value: "8", label: "8 years" },
                              { value: "9", label: "9 years" },
                              { value: "10", label: "10 years" },
                              { value: "10+", label: "10+ years" }
                            ]}
                            value={
                              lead?.total_work_experience
                                ? { 
                                    value: lead.total_work_experience, 
                                    label: lead.total_work_experience === "1" 
                                      ? "1 year" 
                                      : `${lead.total_work_experience} years` 
                                  }
                                : null
                            }
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'total_work_experience' })}
                            placeholder="Select Experience"
                            isClearable
                            inputId="total_work_experience"
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            styles={{
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999
                              })
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Salary Mode</Form.Label>
                          <Select
                            name="salary_mode"
                            options={[
                              { value: 'Bank Transfer', label: 'Bank Transfer' },
                              { value: 'Cash', label: 'Cash' },
                              { value: 'Cheque', label: 'Cheque' }
                            ]}
                            value={
                              lead?.salary_mode
                                ? { value: lead.salary_mode, label: lead.salary_mode }
                                : null
                            }
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'salary_mode' })}
                            placeholder="Select Salary Mode"
                            isClearable
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Office Email</Form.Label>
                          <Form.Control
                            type="email"
                            name="office_email"
                            value={lead?.office_email || ''}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Office Address</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="office_address"
                            value={lead?.office_address || ''}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Office State</Form.Label>
                          <Select
                            name="office_state_id"
                            options={states.map(state => ({ value: state.id, label: state.name }))}
                            value={states.map(s => ({ value: s.id, label: s.name })).find(s => s.value === lead?.office_state_id) || null}
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'office_state_id' })}
                            placeholder="Select State"
                            isLoading={loadingLocations && !states.length} // Show loading initially
                            isClearable
                            inputId="office_state_id"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Office City</Form.Label>
                          <Select
                            name="office_city_id"
                            options={officeCities.map(city => ({ value: city.id, label: city.name }))}
                            value={officeCities.map(c => ({ value: c.id, label: c.name })).find(c => c.value === lead?.office_city_id) || null}
                            onChange={(selectedOption) => handleInputChange(selectedOption, { name: 'office_city_id' })}
                            placeholder="Select City"
                            isDisabled={!lead?.office_state_id || (loadingLocations && !states.length)} // Disable if no office state or states still loading
                            isLoading={loadingLocations && !!lead?.office_state_id && !officeCities.length} // Show loading only when fetching office cities
                            isClearable
                            inputId="office_city_id"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Office Pincode</Form.Label>
                          <Form.Control
                            type="text"
                            name="office_pincode"
                            value={lead?.office_pincode || ''}
                            onChange={(e) => handleInputChange(e)}
                            placeholder="Enter Office Pincode"
                            maxLength={6}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-4 mt-3">
                      <Col md={6} className="d-flex align-items-center">
                        {/* Lead Status Badge */}
                        {lead && (
                          <div className="lead-status-badge-container mb-0 ps-0">
                            <span className='me-2 fw-bold'>Lead status:</span>
                            <div className={`lead-status-badge status-${lead.lead_status || 1}`}>
                              {lead.lead_status === 1 && <FaUser className="me-2" />}
                              {lead.lead_status === 2 && <FaCreditCard className="me-2" />}
                              {lead.lead_status === 3 && <FaUser className="me-2" />}
                              {lead.lead_status === 4 && <FaMapMarkerAlt className="me-2" />}
                              {lead.lead_status === 5 && <FaBriefcase className="me-2" />}
                              {lead.lead_status === 6 && <FaFileAlt className="me-2" />}
                              {lead.lead_status === 7 && <FaCheckCircle className="me-2" />}
                              {lead.lead_status >= 8 && lead.lead_status <= 10 && <FaFileInvoiceDollar className="me-2" />}
                              {lead.lead_status === 11 && <FaTimesCircle className="me-2" />}
                              {lead.lead_status >= 12 && <FaCheckCircle className="me-2" />}
                              {leadStatuses.find(status => status.id === lead.lead_status)?.name || "New Lead"}
                            </div>
                          </div>
                        )}
                      </Col>
                      <Col md={6} className="d-flex justify-content-end align-items-center">
                        <Button 
                          variant="primary"
                          disabled={saving || lead?.lead_status >= 7}
                          onClick={(e) => {
                            e.preventDefault(); // Prevent default form submission
                            handleSubmit(e, 'income');
                          }}
                        >
                          {saving ? (
                            <><FaSave className="me-2" /> Saving...</>
                          ) : lead?.lead_status >= 7 ? (
                            <><FaLock className="me-2" /> Locked</>
                          ) : (
                            <><FaSave className="me-2" /> Save & Continue</>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Documents Tab */}
              <Tab.Pane eventKey="documents">
                <Card className="tab-card">
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Aadhaar Card Number</Form.Label>
                          <Form.Control
                            type="text"
                            name="adhaar_card_no"
                            value={lead?.adhaar_card_no || ''}
                            onChange={handleInputChange}
                            maxLength={12}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>PAN Card Number</Form.Label>
                          <Form.Control
                            type="text"
                            name="pan_card_no"
                            value={lead?.pan_card_no || ''}
                            onChange={handleInputChange}
                            maxLength={10}
                            style={{ textTransform: 'uppercase' }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <hr className="my-4" />
                    <Row className="mt-3">
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Aadhaar Card</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              onChange={(e) => uploadDocumentAndRefresh(e, 'aadhaar')}
                              className={documents.adhaar_card_file ? "border-success" : ""}
                            />
                            {documents.adhaar_card_file && documents.adhaar_card_file.url && (
                              <Button 
                                variant="link" 
                                href={documents.adhaar_card_file.url} 
                                target="_blank" 
                                className="ms-2"
                              >
                                View
                              </Button>
                            )}
                          </div>
                          {documents.adhaar_card_file && (
                            <div className="d-flex align-items-center text-success mt-1">
                              <FaCheckCircle className="me-1" /> 
                              <span>Uploaded {documents.adhaar_card_file.name && `- ${documents.adhaar_card_file.name}`}</span>
                            </div>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Bank Statements</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              onChange={(e) => uploadDocumentAndRefresh(e, 'bankStatements')}
                              className={documents.bank_statements_file ? "border-success" : ""}
                            />
                            {documents.bank_statements_file && documents.bank_statements_file.url && (
                              <Button 
                                variant="link" 
                                href={documents.bank_statements_file.url} 
                                target="_blank" 
                                className="ms-2"
                              >
                                View
                              </Button>
                            )}
                          </div>
                          {documents.bank_statements_file && (
                            <div className="d-flex align-items-center text-success mt-1">
                              <FaCheckCircle className="me-1" /> 
                              <span>Uploaded {documents.bank_statements_file.name && `- ${documents.bank_statements_file.name}`}</span>
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>PAN Card</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              onChange={(e) => uploadDocumentAndRefresh(e, 'pan')}
                              className={documents.pan_card_file ? "border-success" : ""}
                            />
                            {documents.pan_card_file && documents.pan_card_file.url && (
                              <Button 
                                variant="link" 
                                href={documents.pan_card_file.url} 
                                target="_blank" 
                                className="ms-2"
                              >
                                View
                              </Button>
                            )}
                          </div>
                          {documents.pan_card_file && (
                            <div className="d-flex align-items-center text-success mt-1">
                              <FaCheckCircle className="me-1" /> 
                              <span>Uploaded {documents.pan_card_file.name && `- ${documents.pan_card_file.name}`}</span>
                            </div>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-3 mt-3">
                          <Form.Label>Voter ID / Address Proof</Form.Label>
                          <div className="d-flex align-items-center">
                            <Form.Control
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              onChange={(e) => uploadDocumentAndRefresh(e, 'addressProof')}
                              className={documents.voter_id_card_file ? "border-success" : ""}
                            />
                            {documents.voter_id_card_file && documents.voter_id_card_file.url && (
                              <Button 
                                variant="link" 
                                href={documents.voter_id_card_file.url} 
                                target="_blank" 
                                className="ms-2"
                              >
                                View
                              </Button>
                            )}
                          </div>
                          {documents.voter_id_card_file && (
                            <div className="d-flex align-items-center text-success mt-1">
                              <FaCheckCircle className="me-1" /> 
                              <span>Uploaded {documents.voter_id_card_file.name && `- ${documents.voter_id_card_file.name}`}</span>
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    {/* Personal Loan Documents - conditionally shown when Case Type is selected */}
                    {lead && (lead.case_type === "Personal Loan" || lead.case_type === "Home Loan" || lead.case_type === "LAP") && (
                      <>
                        <hr className="my-4" />
                        <h4>Additional Documents for {lead.case_type}</h4>
                        <Row className="mt-3">
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Bank SOA (For BT)</Form.Label>
                              <div className="d-flex align-items-center">
                                <Form.Control
                                  type="file"
                                  accept=".jpg,.jpeg,.png,.pdf"
                                  onChange={(e) => uploadDocumentAndRefresh(e, 'bankSoa')}
                                  className={documents.bank_soa_file ? "border-success" : ""}
                                  disabled={lead?.lead_status >= 7}
                                />
                                {documents.bank_soa_file && documents.bank_soa_file.url && (
                                  <Button 
                                    variant="link" 
                                    href={documents.bank_soa_file.url} 
                                    target="_blank" 
                                    className="ms-2"
                                  >
                                    View
                                  </Button>
                                )}
                              </div>
                              {documents.bank_soa_file && (
                                <div className="d-flex align-items-center text-success mt-1">
                                  <FaCheckCircle className="me-1" /> 
                                  <span>Uploaded {documents.bank_soa_file.name && `- ${documents.bank_soa_file.name}`}</span>
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                          
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Form 16</Form.Label>
                              <div className="d-flex align-items-center">
                                <Form.Control
                                  type="file"
                                  accept=".jpg,.jpeg,.png,.pdf"
                                  onChange={(e) => uploadDocumentAndRefresh(e, 'form16')}
                                  className={documents.form_16_file ? "border-success" : ""}
                                  disabled={lead?.lead_status >= 7}
                                />
                                {documents.form_16_file && documents.form_16_file.url && (
                                  <Button 
                                    variant="link" 
                                    href={documents.form_16_file.url} 
                                    target="_blank" 
                                    className="ms-2"
                                  >
                                    View
                                  </Button>
                                )}
                              </div>
                              {documents.form_16_file && (
                                <div className="d-flex align-items-center text-success mt-1">
                                  <FaCheckCircle className="me-1" /> 
                                  <span>Uploaded {documents.form_16_file.name && `- ${documents.form_16_file.name}`}</span>
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                          
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Office ID Card</Form.Label>
                              <div className="d-flex align-items-center">
                                <Form.Control
                                  type="file"
                                  accept=".jpg,.jpeg,.png,.pdf"
                                  onChange={(e) => uploadDocumentAndRefresh(e, 'officeId')}
                                  className={documents.office_id_card_file ? "border-success" : ""}
                                  disabled={lead?.lead_status >= 7}
                                />
                                {documents.office_id_card_file && documents.office_id_card_file.url && (
                                  <Button 
                                    variant="link" 
                                    href={documents.office_id_card_file.url} 
                                    target="_blank" 
                                    className="ms-2"
                                  >
                                    View
                                  </Button>
                                )}
                              </div>
                              {documents.office_id_card_file && (
                                <div className="d-flex align-items-center text-success mt-1">
                                  <FaCheckCircle className="me-1" /> 
                                  <span>Uploaded {documents.office_id_card_file.name && `- ${documents.office_id_card_file.name}`}</span>
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                          
                          {/* Home Loan and LAP specific documents */}
                          {(lead.case_type === "Home Loan" || lead.case_type === "LAP") && (
                            <>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Copy of ATS/Title docs of property</Form.Label>
                                  <div className="d-flex align-items-center">
                                    <Form.Control
                                      type="file"
                                      accept=".jpg,.jpeg,.png,.pdf"
                                      onChange={(e) => uploadLoanDocumentAndRefresh(e, 'propertyAtsTitle')}
                                      className={documents.property_ats_title_file ? "border-success" : ""}
                                      disabled={lead?.lead_status >= 7}
                                    />
                                    {documents.property_ats_title_file && documents.property_ats_title_file.url && (
                                      <Button 
                                        variant="link" 
                                        href={documents.property_ats_title_file.url} 
                                        target="_blank" 
                                        className="ms-2"
                                      >
                                        View
                                      </Button>
                                    )}
                                  </div>
                                  {documents.property_ats_title_file && (
                                    <div className="d-flex align-items-center text-success mt-1">
                                      <FaCheckCircle className="me-1" /> 
                                      <span>Uploaded {documents.property_ats_title_file.name && `- ${documents.property_ats_title_file.name}`}</span>
                                    </div>
                                  )}
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Loan Declaration Sheet</Form.Label>
                                  <div className="d-flex align-items-center">
                                    <Form.Control
                                      type="file"
                                      accept=".jpg,.jpeg,.png,.pdf"
                                      onChange={(e) => uploadLoanDocumentAndRefresh(e, 'loanDeclarationSheet')}
                                      className={documents.loan_declaration_sheet_file ? "border-success" : ""}
                                      disabled={lead?.lead_status >= 7}
                                    />
                                    {documents.loan_declaration_sheet_file && documents.loan_declaration_sheet_file.url && (
                                      <Button 
                                        variant="link" 
                                        href={documents.loan_declaration_sheet_file.url} 
                                        target="_blank" 
                                        className="ms-2"
                                      >
                                        View
                                      </Button>
                                    )}
                                  </div>
                                  {documents.loan_declaration_sheet_file && (
                                    <div className="d-flex align-items-center text-success mt-1">
                                      <FaCheckCircle className="me-1" /> 
                                      <span>Uploaded {documents.loan_declaration_sheet_file.name && `- ${documents.loan_declaration_sheet_file.name}`}</span>
                                    </div>
                                  )}
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Other Property Documents</Form.Label>
                                  <div className="d-flex align-items-center">
                                    <Form.Control
                                      type="file"
                                      accept=".jpg,.jpeg,.png,.pdf"
                                      onChange={(e) => uploadLoanDocumentAndRefresh(e, 'otherPropertyDocs')}
                                      className={documents.additional_documents ? "border-success" : ""}
                                      disabled={lead?.lead_status >= 7}
                                    />
                                    {documents.additional_documents && documents.additional_documents.url && (
                                      <Button 
                                        variant="link" 
                                        href={documents.additional_documents.url} 
                                        target="_blank" 
                                        className="ms-2"
                                      >
                                        View
                                      </Button>
                                    )}
                                  </div>
                                  {documents.additional_documents && (
                                    <div className="d-flex align-items-center text-success mt-1">
                                      <FaCheckCircle className="me-1" /> 
                                      <span>Uploaded {documents.additional_documents.name && `- ${documents.additional_documents.name}`}</span>
                                    </div>
                                  )}
                                </Form.Group>
                              </Col>
                            </>
                          )}
                        </Row>
                      </>
                    )}
                    
                    {/* Business Loan Documents - conditionally shown when Business Loan is selected */}
                    {lead && lead.case_type === "Business Loan" && lead.person_type === "Business" && lead.business_type ? (
                      <>
                        <hr className="my-4" />
                        <h4>Additional Documents</h4>
                        <Row className="mt-4">
                          <Col md={12}>
                            <BusinessLoanDocuments 
                              leadId={leadId} 
                              businessType={lead.business_type}
                              isLeadLocked={lead?.lead_status >= 7} 
                            />
                          </Col>
                        </Row>
                      </>
                    ) : lead && lead.case_type === "Business Loan" && (
                      <>
                        <hr className="my-4" />
                        <h4>Additional Documents</h4>
                        <Alert variant="info">
                          Business Loan documents will appear here when you select "Business Loan" as case type, "Business" as person type, and choose a business type.
                        </Alert>
                      </>
                    )}
                    
                    <Row className="mb-4 mt-3">
                      <Col md={6} className="d-flex align-items-center">
                        {/* Lead Status Badge */}
                        {lead && (
                          <div className="lead-status-badge-container mb-0 ps-0">
                            <span className='me-2 fw-bold'>Lead status:</span>
                            <div className={`lead-status-badge status-${lead.lead_status || 1}`}>
                              {lead.lead_status === 1 && <FaUser className="me-2" />}
                              {lead.lead_status === 2 && <FaCreditCard className="me-2" />}
                              {lead.lead_status === 3 && <FaUser className="me-2" />}
                              {lead.lead_status === 4 && <FaMapMarkerAlt className="me-2" />}
                              {lead.lead_status === 5 && <FaBriefcase className="me-2" />}
                              {lead.lead_status === 6 && <FaFileAlt className="me-2" />}
                              {lead.lead_status === 7 && <FaCheckCircle className="me-2" />}
                              {lead.lead_status >= 8 && lead.lead_status <= 10 && <FaFileInvoiceDollar className="me-2" />}
                              {lead.lead_status === 11 && <FaTimesCircle className="me-2" />}
                              {lead.lead_status >= 12 && <FaCheckCircle className="me-2" />}
                              {leadStatuses.find(status => status.id === lead.lead_status)?.name || "New Lead"}
                            </div>
                          </div>
                        )}
                      </Col>
                      <Col md={6} className="d-flex justify-content-end align-items-center">
                        <Button 
                          variant="primary"
                          disabled={saving || lead?.lead_status >= 7}
                          onClick={(e) => {
                            e.preventDefault(); // Prevent default form submission
                            handleSubmit(e, 'documents');
                          }}
                        >
                          {saving ? (
                            <><FaSave className="me-2" /> Saving...</>
                          ) : lead?.lead_status >= 7 ? (
                            <><FaLock className="me-2" /> Locked</>
                          ) : (
                            <><FaSave className="me-2" /> Save & Continue</>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Application Details Tab */}
              <Tab.Pane eventKey="application">
                <Card className="tab-card">
                  <Card.Body>
                    <h5 className="mb-4">Application Details</h5>

                    <Row className="mt-4">
                      <Col md={12}>
                        <h5 className="mb-3">Lead Information Summary</h5>
                        <Table striped bordered hover responsive>
                          <tbody>
                            <tr>
                              <th width="25%">Full Name</th>
                              <td>{lead?.name || '-'}</td>
                              <th width="25%">Email</th>
                              <td>{lead?.email || '-'}</td>
                            </tr>
                            <tr>
                              <th>Mobile</th>
                              <td>{lead?.mobile || '-'}</td>
                              <th>Date of Birth</th>
                              <td>{lead?.date_of_birth ? new Date(lead.date_of_birth).toLocaleDateString() : '-'}</td>
                            </tr>
                            <tr>
                              <th>Gender</th>
                              <td>{lead?.gender || '-'}</td>
                              <th>Income (Monthly In Hand)</th>
                              <td>{lead?.monthly_inhand ? `₹${Number(lead.monthly_inhand).toLocaleString()}` : '-'}</td>
                            </tr>
                            <tr>
                              <th>Employer</th>
                              <td>{lead?.employer_name || '-'}</td>
                              <th>Designation</th>
                              <td>{lead?.designation || '-'}</td>
                            </tr>
                            <tr>
                              <th>Total Work Experience</th>
                              <td>{lead?.total_work_experience ? `${lead.total_work_experience} year(s)` : '-'}</td>
                              <th>Address</th>
                              <td colSpan="3">{lead?.address || '-'}</td>
                            </tr>
                          </tbody>
                        </Table>
                      </Col>
                    </Row>

                    {/* Uploaded Documents Section */}
                    <Row className="mt-4">
                      <Col md={12}>
                        <h5 className="mb-3">Uploaded Documents</h5>
                        <Card>
                          <Card.Body>
                            <Row>
                              {documents.adhaar_card_file && (
                                <Col md={6} className="mb-3">
                                  <div className="document-card p-3 border rounded">
                                    <h6>Aadhaar Card</h6>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <span className="text-muted">{documents.adhaar_card_file.name || "Document"}</span>
                                      <Button 
                                        variant="primary"
                                        size="sm"
                                        href={documents.adhaar_card_file.url}
                                        target="_blank"
                                      >
                                        View
                                      </Button>
                                    </div>
                                  </div>
                                </Col>
                              )}
                              
                              {documents.pan_card_file && (
                                <Col md={6} className="mb-3">
                                  <div className="document-card p-3 border rounded">
                                    <h6>PAN Card</h6>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <span className="text-muted">{documents.pan_card_file.name || "Document"}</span>
                                      <Button 
                                        variant="primary"
                                        size="sm"
                                        href={documents.pan_card_file.url}
                                        target="_blank"
                                      >
                                        View
                                      </Button>
                                    </div>
                                  </div>
                                </Col>
                              )}
                              
                              {documents.voter_id_card_file && (
                                <Col md={6} className="mb-3">
                                  <div className="document-card p-3 border rounded">
                                    <h6>Address Proof</h6>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <span className="text-muted">{documents.voter_id_card_file.name || "Document"}</span>
                                      <Button 
                                        variant="primary"
                                        size="sm"
                                        href={documents.voter_id_card_file.url}
                                        target="_blank"
                                      >
                                        View
                                      </Button>
                                    </div>
                                  </div>
                                </Col>
                              )}
                              
                              {documents.bank_statements_file && (
                                <Col md={6} className="mb-3">
                                  <div className="document-card p-3 border rounded">
                                    <h6>Bank Statements</h6>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <span className="text-muted">{documents.bank_statements_file.name || "Document"}</span>
                                      <Button 
                                        variant="primary"
                                        size="sm"
                                        href={documents.bank_statements_file.url}
                                        target="_blank"
                                      >
                                        View
                                      </Button>
                                    </div>
                                  </div>
                                </Col>
                              )}
                              
                              {/* Show Personal Loan specific documents if that's the case type */}
                              {(lead?.case_type === "Personal Loan" || lead?.case_type === "LAP" || lead?.case_type === "Home Loan") && documents.bank_soa_file && (
                                <Col md={6} className="mb-3">
                                  <div className="document-card p-3 border rounded">
                                    <h6>Bank SOA (For BT)</h6>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <span className="text-muted">{documents.bank_soa_file.name || "Document"}</span>
                                      <Button 
                                        variant="primary"
                                        size="sm"
                                        href={documents.bank_soa_file.url}
                                        target="_blank"
                                      >
                                        View
                                      </Button>
                                    </div>
                                  </div>
                                </Col>
                              )}
                              
                              {(lead?.case_type === "Personal Loan" || lead?.case_type === "LAP" || lead?.case_type === "Home Loan") && documents.form_16_file && (
                                <Col md={6} className="mb-3">
                                  <div className="document-card p-3 border rounded">
                                    <h6>Form 16</h6>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <span className="text-muted">{documents.form_16_file.name || "Document"}</span>
                                      <Button 
                                        variant="primary"
                                        size="sm"
                                        href={documents.form_16_file.url}
                                        target="_blank"
                                      >
                                        View
                                      </Button>
                                    </div>
                                  </div>
                                </Col>
                              )}
                              
                              {(lead?.case_type === "Personal Loan" || lead?.case_type === "LAP" || lead?.case_type === "Home Loan") && documents.office_id_card_file && (
                                <Col md={6} className="mb-3">
                                  <div className="document-card p-3 border rounded">
                                    <h6>Office ID Card</h6>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <span className="text-muted">{documents.office_id_card_file.name || "Document"}</span>
                                      <Button 
                                        variant="primary"
                                        size="sm"
                                        href={documents.office_id_card_file.url}
                                        target="_blank"
                                      >
                                        View
                                      </Button>
                                    </div>
                                  </div>
                                </Col>
                              )}

                              {!documents.adhaar_card_file && !documents.pan_card_file && 
                               !documents.voter_id_card_file && !documents.bank_statements_file &&
                               !documents.bank_soa_file && !documents.form_16_file && !documents.office_id_card_file && 
                               !documents.property_ats_title_file && !documents.loan_declaration_sheet_file && !documents.additional_documents && (
                                <Col md={12}>
                                  <Alert variant="info" className="mb-0">
                                    No documents have been uploaded for this lead.
                                  </Alert>
                                </Col>
                              )}
                            </Row>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-4">
                          <Form.Label><strong>Associated Cases</strong></Form.Label>
                          {loadingCases ? (
                            <div className="text-center my-4">
                              <Spinner animation="border" variant="primary" />
                              <p className="mt-2">Loading cases...</p>
                            </div>
                          ) : cases.length > 0 ? (
                            <Table responsive hover className="mt-2">
                              <thead className="table-light">
                                <tr>
                                  <th>
                                    Select Cases
                                  </th>
                                  <th>Case ID</th>
                                  <th>Type</th>
                                  <th>Application No</th>
                                  <th>Product</th>
                                  <th>Amount</th>
                                  <th>Purpose</th>
                                  <th>Tenure</th>
                                  <th>Status</th>
                                  <th>Created On</th>
                                </tr>
                              </thead>
                              <tbody>
                                {cases.map((caseItem) => (
                                  <tr key={caseItem.id}>
                                    <td>
                                      <Form.Check
                                        type="checkbox"
                                        id={`select-case-${caseItem.id}`}
                                        checked={selectedCases.includes(caseItem.id)}
                                        onChange={() => handleCaseSelection(caseItem.id)}
                                      />
                                    </td>
                                    <td>#{caseItem.id}</td>
                                    <td>
                                      <span className="d-flex align-items-center">
                                        {caseItem.case_type === "Credit Card" ? (
                                          <FaCreditCard className="me-2" />
                                        ) : (
                                          <FaFileInvoiceDollar className="me-2" />
                                        )}
                                        {getCaseTypeName(caseItem.product.category)}
                                      </span>
                                    </td>
                                    <td>{caseItem.application_no}</td>
                                    <td>{caseItem.product.name}</td>
                                    <td>{caseItem.loan_amount}</td>
                                    <td>{caseItem.loan_purpose}</td>
                                    <td>{caseItem.tenure}</td>
                                    <td>
                                      <Badge bg={getStatusBadgeVariant(caseItem.status.name)} pill>
                                        {caseItem.status.name}
                                      </Badge>
                                    </td>
                                    <td>{formatDate(caseItem.created_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <Alert variant="info" className="mb-0">
                              No cases associated with this lead.
                            </Alert>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                  
                    
                    <Row className="mt-4">
                      <Col md={12} className="text-center">
                        <Button
                          variant="primary"
                          disabled={saving || selectedCases.length === 0}
                          className="px-5"
                          onClick={handlePushToBackoffice}
                        >
                          {saving ? (
                            <><FaSave className="me-2" /> Processing...</>
                          ) : (
                            <><FaSave className="me-2" /> Push Selected Cases to Backoffice</>
                          )}
                        </Button>
                        {selectedCases.length === 0 && (
                          <div className="text-danger mt-2">
                            <small>
                              <FaInfoCircle className="me-1" />
                              Please select at least one case to push to backoffice
                            </small>
                          </div>
                        )}
                        {selectedCases.length > 0 && (
                          <div className="text-success mt-2">
                            <small>
                              <FaCheckCircle className="me-1" />
                              {selectedCases.length} case{selectedCases.length > 1 ? 's' : ''} selected
                            </small>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
            
          </Form>
        </div>
              </Tab.Container>
            </Container>
          </div>
        </div>
        
        {/* Search Modal for Mobile */}
        <Modal 
          show={showSearchModal} 
          onHide={() => setShowSearchModal(false)}
          className="search-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Search Results</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mobile-search-container mb-3">
              <div className="search-input-container">
                <FaSearch className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search leads, products, invoices..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyPress={handleSearchKeyPress}
                  autoFocus
                />
                {searchQuery && (
                  <button className="clear-search-btn" onClick={clearSearch}>
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
            
            {isSearching ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-3">Searching...</p>
              </div>
            ) : searchResults.length > 0 ? (
              <SearchResults 
                results={searchResults} 
                onResultClick={handleSearchResultClick} 
              />
            ) : searchQuery.trim().length > 1 ? (
              <div className="text-center py-5">
                <p>No results found for "{searchQuery}".</p>
              </div>
            ) : (
              <div className="text-center py-5 text-muted">
                <FaSearch size={32} />
                <p className="mt-3">Enter at least 2 characters to search</p>
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>
    );
}

export default LeadDetails;
